import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { fetchApi } from '../api';
import { getSocket } from '../socket';
import { ChatListPanel } from './chats/ChatListPanel';
import { ChatMessagePanel } from './chats/ChatMessagePanel';
import { ChatDetailsSidebar } from './chats/ChatDetailsSidebar';
import { OutgoingMessage } from './chats/messageTypes';
import { CHANNELS } from '../constants/channels';

const avatarColor = (name: string) => {
  const colors = ['#f43f5e', '#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ec4899'];
  return colors[name.length % colors.length];
};

const initials = (n: string) => n.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();

export default function Chats({
  autopilot,
  setAutopilot,
  selectedChatId
}: {
  autopilot: boolean;
  setAutopilot: (v: boolean) => void;
  selectedChatId?: number | null;
}) {
  const [conversations, setConversations] = useState<any[]>([]);
  const [conversationPage, setConversationPage] = useState(1);
  const [conversationTotal, setConversationTotal] = useState(0);
  const [messages, setMessages] = useState<any[]>([]);
  const [messagePage, setMessagePage] = useState(1);
  const [messageTotal, setMessageTotal] = useState(0);
  const [deals, setDeals] = useState<any[]>([]);
  const [catalog, setCatalog] = useState<any[]>([]);

  const [activeChatId, setActiveChatId] = useState<number | null>(selectedChatId || null);
  const activeChatRef = useRef<number | null>(null);
  const isInitialScrollRef = useRef(true);
  const olderScrollHeightRef = useRef<number | null>(null);

  useEffect(() => { if (selectedChatId) { setActiveChatId(selectedChatId); } }, [selectedChatId]);

  useEffect(() => {
    if (activeChatId) { document.body.classList.add('chat-open'); }
    else { document.body.classList.remove('chat-open'); }
    return () => { document.body.classList.remove('chat-open'); };
  }, [activeChatId]);

  useEffect(() => { activeChatRef.current = activeChatId; }, [activeChatId]);

  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [input, setInput] = useState<string>('');
  const [isTyping, setIsTyping] = useState(false);

  const [dealTitle, setDealTitle] = useState('');
  const [dealAmount, setDealAmount] = useState<string>('');
  const [dealDiscountPercent, setDealDiscountPercent] = useState<number>(0);
  const [dealStage, setDealStage] = useState<string>('Новая заявка');
  const [dealItems, setDealItems] = useState<any[]>([]);
  const [selectedProdId, setSelectedProdId] = useState<string>('');
  const [editingDealId, setEditingDealId] = useState<number | null>(null);
  const [isDealFormOpen, setIsDealFormOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const msgEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchApi('/deals').then(data => setDeals(Array.isArray(data) ? data : (data?.data || []))).catch(console.error);
    fetchApi('/products').then(data => setCatalog(Array.isArray(data) ? data : (data?.data || []))).catch(console.error);

    const socket = getSocket();
    const handler = (data: any) => {
      if (!data) return;
      const msgObj = data.message || data;
      const targetConvId = data.conversationId || data.conversation_id || msgObj.conversationId || msgObj.conversation_id;
      if (!targetConvId) return;
      const msgText = msgObj.text || '';
      const msgDirection = msgObj.direction || 'in';

      setConversations(prev => {
        const prevList = Array.isArray(prev) ? prev : ((prev as any)?.data || []);
        let updated = false;
        const newList = prevList.map((c: any) => {
          if (Number(c.id) === Number(targetConvId)) {
            updated = true;
            const nowIso = new Date().toISOString();
            return { ...c, last_text: msgText, last_sent: nowIso, updatedAt: nowIso, lastTime: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }), unread: (msgDirection === 'out' || Number(activeChatRef.current) === Number(c.id)) ? 0 : (c.unread || 0) + 1 };
          }
          return c;
        });
        if (!updated) { fetchApi('/conversations?paginated=true&limit=100&page=1').then(d => { setConversations(d?.data || []); setConversationTotal(d?.total || 0); setConversationPage(1); }); }
        return newList;
      });

      setActiveChatId(currentActiveId => {
        if (currentActiveId && Number(currentActiveId) === Number(targetConvId)) {
          setMessages(prev => {
            const prevList = Array.isArray(prev) ? prev : ((prev as any)?.data || []);
            if (msgObj.id && prevList.some((m: any) => Number(m.id) === Number(msgObj.id))) return prevList;
            return [...prevList, msgObj];
          });
        }
        return currentActiveId;
      });
    };

    const handleConvUpdated = (data: any) => { setConversations(prev => { const prevList = Array.isArray(prev) ? prev : ((prev as any)?.data || []); return prevList.map((c: any) => (Number(c.id) === Number(data.id) ? { ...c, ...data } : c)); }); };
    const handleDealNew = (deal: any) => { setDeals(prev => { const prevList = Array.isArray(prev) ? prev : ((prev as any)?.data || []); return [deal, ...prevList.filter((d: any) => d.id !== deal.id)]; }); };

    if (socket) {
      socket.on('message:new', handler);
      socket.on('conversation:updated', handleConvUpdated);
      socket.on('deal:new', handleDealNew);
      socket.on('message:delivery', (delivery: any) => { setMessages(prev => prev.map(message => Number(message.id) === Number(delivery.id) ? { ...message, ...delivery } : message)); });
    }

    const handleGlobalSearch = (e: any) => { setSearch(e.detail || ''); };
    window.addEventListener('global:search', handleGlobalSearch);

    return () => {
      if (socket) { socket.off('message:new', handler); socket.off('conversation:updated', handleConvUpdated); socket.off('deal:new', handleDealNew); socket.off('message:delivery'); }
      window.removeEventListener('global:search', handleGlobalSearch);
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams({ paginated: 'true', limit: '100', page: '1' });
      if (search.trim()) params.set('q', search.trim());
      fetchApi(`/conversations?${params.toString()}`, { signal: abortController.signal })
        .then(data => { if (abortController.signal.aborted) return; setConversations(data?.data || []); setConversationTotal(data?.total || 0); setConversationPage(1); })
        .catch(error => { if (!abortController.signal.aborted) console.error(error); });
    }, 250);
    return () => { window.clearTimeout(timer); abortController.abort(); };
  }, [search]);

  useEffect(() => {
    if (!activeChatId) return;
    const requestedConversationId = activeChatId;
    const abortController = new AbortController();
    isInitialScrollRef.current = true;
    setMessages([]); setMessagePage(1); setMessageTotal(0);
    fetchApi(`/conversations/${requestedConversationId}/messages?paginated=true&limit=200`, { signal: abortController.signal })
      .then(data => { if (!abortController.signal.aborted && Number(activeChatRef.current) === Number(requestedConversationId)) { setMessages(Array.isArray(data) ? data : (data?.data || [])); setMessageTotal(Array.isArray(data) ? data.length : (data?.total || 0)); } })
      .catch(error => { if (!abortController.signal.aborted) console.error(error); });
    return () => abortController.abort();
  }, [activeChatId]);

  useLayoutEffect(() => {
    if (!msgEndRef.current) return;
    const container = msgEndRef.current.parentElement;
    if (olderScrollHeightRef.current !== null && container) { container.scrollTop += container.scrollHeight - olderScrollHeightRef.current; olderScrollHeightRef.current = null; return; }
    if (isInitialScrollRef.current) {
      if (container) { container.scrollTop = container.scrollHeight; }
      msgEndRef.current.scrollIntoView({ behavior: 'auto' });
      if (messages.length > 0) { isInitialScrollRef.current = false; }
      requestAnimationFrame(() => { if (container) { container.scrollTop = container.scrollHeight; } });
    } else { msgEndRef.current.scrollIntoView({ behavior: 'smooth' }); }
  }, [messages, isTyping]);

  const activeChat = useMemo(() => { const list = Array.isArray(conversations) ? conversations : []; return list.find((c: any) => c.id === activeChatId); }, [conversations, activeChatId]);
  const activeChatDeals = useMemo(() => { if (!activeChatId) return []; const list = Array.isArray(deals) ? deals : []; return list.filter((d: any) => Number(d.conversationId || d.conversation_id) === Number(activeChatId)); }, [deals, activeChatId]);
  const chatTags = useMemo(() => { if (!activeChat || !activeChat.tags) return []; try { return typeof activeChat.tags === 'string' ? JSON.parse(activeChat.tags) : activeChat.tags; } catch { return []; } }, [activeChat]);

  const filteredList = useMemo(() => {
    const q = search.toLowerCase();
    const list = Array.isArray(conversations) ? conversations : [];
    return list.filter((c: any) => {
      const matchSearch = (c.contactName || '').toLowerCase().includes(q) || (c.last_text || '').toLowerCase().includes(q);
      if (!matchSearch) return false;
      if (filter === 'all') return true;
      if (filter === 'unread') return (c.unread || 0) > 0;
      if (filter === 'ai') return !!c.aiEnabled;
      if (filter === 'hot') return c.temp === 'hot';
      const cType = c.channelType || c.channel?.type || 'widget';
      return cType === filter;
    }).sort((a: any, b: any) => {
      const getTime = (item: any) => { const timeStr = item.last_sent || item.updatedAt || item.createdAt; if (timeStr) { const t = new Date(timeStr).getTime(); if (!isNaN(t) && t > 0) return t; } return 0; };
      const diff = getTime(b) - getTime(a);
      if (diff !== 0) return diff;
      return Number(b.id) - Number(a.id);
    });
  }, [conversations, filter, search]);

  const unreadCount = useMemo(() => (Array.isArray(conversations) ? conversations : []).filter((conversation: any) => (conversation.unread || 0) > 0).length, [conversations]);

  const loadMoreConversations = () => {
    const nextPage = conversationPage + 1;
    fetchApi(`/conversations?paginated=true&limit=100&page=${nextPage}`)
      .then(data => { setConversations(prev => { const existing = new Set(prev.map(conversation => conversation.id)); return [...prev, ...(data.data || []).filter((conversation: any) => !existing.has(conversation.id))]; }); setConversationTotal(data.total || 0); setConversationPage(nextPage); })
      .catch(console.error);
  };

  const loadOlderMessages = () => {
    if (!activeChatId || messages.length >= messageTotal) return;
    const nextPage = messagePage + 1;
    const container = msgEndRef.current?.parentElement;
    olderScrollHeightRef.current = container?.scrollHeight ?? null;
    fetchApi(`/conversations/${activeChatId}/messages?paginated=true&limit=200&page=${nextPage}`)
      .then(data => { setMessages(prev => { const existing = new Set(prev.map(message => message.id)); return [...(data?.data || []).filter((message: any) => !existing.has(message.id)), ...prev]; }); setMessagePage(nextPage); setMessageTotal(data?.total || 0); })
      .catch(console.error);
  };

  const handleOpenChat = (id: number) => {
    activeChatRef.current = id;
    setActiveChatId(id);
    setConversations(prev => prev.map(c => Number(c.id) === Number(id) ? { ...c, unread: 0 } : c));
    document.body.classList.add('chat-open');
    setIsDealFormOpen(false); setIsDetailsOpen(false);
    const socket = getSocket();
    if (socket) { socket.emit('conversation:open', { conversationId: id }); }
    fetchApi(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ unread: 0 }) }).then(() => window.dispatchEvent(new Event('chat:read'))).catch(console.error);
  };

  const backToChats = () => { setIsDetailsOpen(false); document.body.classList.remove('chat-open'); };
  const toggleChatAi = (id: number, enabled: boolean) => { fetchApi(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ ai_enabled: enabled }) }).then(() => { setConversations(prev => prev.map(c => c.id === id ? { ...c, aiEnabled: enabled } : c)); }).catch(console.error); };

  /**
   * The client card lets the manager edit the name, the phone, the city, the
   * temperature, the tags and the assignee. The payload used to contain only
   * `temp` and `tags`, so everything else was accepted by the form and silently
   * dropped. Every editable field is forwarded now — and the local copy is
   * updated from the same list, so the card does not snap back.
   */
  const updateChatField = (id: number, fields: Record<string, unknown>) => {
    const payload: Record<string, unknown> = {};
    if (fields.contactName !== undefined) payload.contactName = fields.contactName;
    if (fields.contactPhone !== undefined) payload.contactPhone = fields.contactPhone;
    if (fields.contactCity !== undefined) payload.contactCity = fields.contactCity;
    if (fields.temp !== undefined) payload.temp = fields.temp;
    if (fields.tags !== undefined) payload.tags = fields.tags;
    if (fields.assignedToId !== undefined) payload.assignedToId = fields.assignedToId;
    if (Object.keys(payload).length === 0) return;

    fetchApi(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
      .then(() => {
        setConversations(prev => prev.map(c => {
          if (c.id !== id) return c;
          const updated: any = { ...c };
          if (fields.contactName !== undefined) updated.contactName = fields.contactName;
          if (fields.contactPhone !== undefined) updated.contactPhone = fields.contactPhone;
          if (fields.contactCity !== undefined) updated.contactCity = fields.contactCity;
          if (fields.temp !== undefined) updated.temp = fields.temp;
          if (fields.tags !== undefined) updated.tags = JSON.stringify(fields.tags);
          if (fields.assignedToId !== undefined) updated.assignedToId = fields.assignedToId;
          return updated;
        }));
      })
      .catch(console.error);
  };

  const handleAddItemToDeal = () => {
    if (!selectedProdId) return;
    const prod = catalog.find(p => p.id === Number(selectedProdId));
    if (!prod) return;
    setDealItems(prev => { const existing = prev.find(i => i.productId === prod.id); let next; if (existing) { next = prev.map(i => i.productId === prod.id ? { ...i, qty: i.qty + 1 } : i); } else { next = [...prev, { productId: prod.id, name: prod.name, price: prod.price || 0, qty: 1 }]; } const autoSum = next.reduce((sum, item) => sum + item.price * item.qty, 0); setDealAmount(String(autoSum)); return next; });
    setSelectedProdId('');
  };

  const handleRemoveItemFromDeal = (prodId: number) => { setDealItems(prev => { const next = prev.filter(i => i.productId !== prodId); const autoSum = next.reduce((sum, item) => sum + item.price * item.qty, 0); setDealAmount(String(autoSum)); return next; }); };
  const handleUpdateItemQty = (prodId: number, qty: number) => { if (qty <= 0) { handleRemoveItemFromDeal(prodId); return; } setDealItems(prev => { const next = prev.map(i => i.productId === prodId ? { ...i, qty } : i); const autoSum = next.reduce((sum, item) => sum + item.price * item.qty, 0); setDealAmount(String(autoSum)); return next; }); };

  const handleSaveDeal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeChatId || !dealTitle.trim()) return;
    const itemsSum = dealItems.reduce((sum, item) => sum + (item.price || 0) * (item.qty || 1), 0);
    const parsedAmount = parseFloat(dealAmount) || 0;
    const originalAmount = dealItems.length > 0 ? itemsSum : parsedAmount;
    const discountPercent = Math.min(100, Math.max(0, dealDiscountPercent || 0));
    const discountAmount = Math.round((originalAmount * (discountPercent / 100)) * 100) / 100;
    const finalAmount = Math.max(0, originalAmount - discountAmount);
    const payload = { conversation_id: activeChatId, title: dealTitle.trim(), originalAmount, discountPercent, discountAmount, amount: finalAmount, stage: dealStage, items: dealItems, status: dealStage === 'Выиграна' ? 'won' : dealStage === 'Проиграна' ? 'lost' : 'open' };
    if (editingDealId) { fetchApi(`/deals/${editingDealId}`, { method: 'PATCH', body: JSON.stringify(payload) }).then(updatedDeal => { setDeals(prev => prev.map(d => d.id === editingDealId ? updatedDeal : d)); resetDealForm(); }).catch(console.error); }
    else { fetchApi('/deals', { method: 'POST', body: JSON.stringify(payload) }).then(newDeal => { setDeals(prev => [newDeal, ...prev]); resetDealForm(); }).catch(console.error); }
  };

  const resetDealForm = () => { setDealTitle(''); setDealAmount(''); setDealDiscountPercent(0); setDealStage('Новая заявка'); setDealItems([]); setEditingDealId(null); setIsDealFormOpen(false); };
  const handleEditDealClick = (deal: any) => { setEditingDealId(deal.id); setDealTitle(deal.title || ''); setDealAmount(String(deal.amount || 0)); setDealDiscountPercent(deal.discountPercent || 0); setDealStage(deal.stage || 'Новая заявка'); setDealItems(deal.items || []); setIsDealFormOpen(true); };
  const handleUpdateDealStatus = (dealId: number, status: 'won' | 'lost') => { fetchApi(`/deals/${dealId}`, { method: 'PATCH', body: JSON.stringify({ status }) }).then(updatedDeal => { setDeals(prev => prev.map(d => d.id === dealId ? updatedDeal : d)); }).catch(console.error); };

  /**
   * The payload always carries its `kind`, so an internal note cannot reach the
   * customer by omission — the server requires the same field and stores a note
   * with `internal: true`, which no delivery path will pick up.
   */
  const sendText = (payload: OutgoingMessage, existingTempId?: number) => {
    const text = payload.text.trim();
    if (!text || !activeChatId) return;
    const conversationId = activeChatId;
    const isNote = payload.kind === 'note';
    const tempMsg = {
      id: existingTempId || Date.now(),
      conversationId,
      direction: 'out',
      author: 'manager',
      text,
      internal: isNote,
      sentAt: new Date().toISOString(),
      deliveryStatus: isNote ? 'internal' : 'queued',
      clientOnly: true,
    };
    setMessages(prev => existingTempId ? prev.map(message => message.id === existingTempId ? tempMsg : message) : [...prev, tempMsg]);
    fetchApi(`/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify({ text, kind: payload.kind }) })
      .then(realMsg => {
        if (!realMsg?.id) throw new Error('Сервер не сохранил сообщение');
        setMessages(prev => { const list = Array.isArray(prev) ? prev : []; if (list.some(message => message.id === realMsg.id)) { return list.filter(message => message.id !== tempMsg.id); } return list.map(message => message.id === tempMsg.id ? realMsg : message); });
      })
      .catch(error => { console.error(error); setMessages(prev => prev.map(message => message.id === tempMsg.id ? { ...message, deliveryStatus: 'failed', deliveryError: error.message, clientOnly: true } : message)); });
  };

  const handleSend = (payload: OutgoingMessage) => {
    if (!payload.text.trim()) return;
    setInput('');
    sendText(payload);
  };

  const retryMessage = (messageId: number) => {
    const message = messages.find(item => item.id === messageId);
    // A note was never sent anywhere, so "повторить" would be meaningless.
    if (message?.internal) return;
    if (message?.clientOnly) { sendText({ kind: 'reply', text: message.text }, messageId); return; }
    setMessages(prev => prev.map(item => item.id === messageId ? { ...item, deliveryStatus: 'queued' } : item));
    fetchApi(`/conversations/${activeChatId}/messages/${messageId}/retry`, { method: 'POST', body: JSON.stringify({}) }).catch(error => { setMessages(prev => prev.map(item => item.id === messageId ? { ...item, deliveryStatus: 'failed', deliveryError: error.message } : item)); });
  };

  const aiRespondClick = () => {
    if (!activeChat) return;
    setIsTyping(true);
    fetchApi('/ai/playground', { method: 'POST', body: JSON.stringify({ conversationId: activeChat.id, text: messages[messages.length - 1]?.text || 'Привет' }) }).then(res => { setInput(res.text); setIsTyping(false); }).catch(() => setIsTyping(false));
  };

  return (
    <section className="view active" id="view-chats">
      <div className={`chats-layout ${activeChat ? 'has-chat' : 'is-empty'}`}>
        <ChatListPanel filteredList={filteredList} activeChatId={activeChatId} search={search} setSearch={setSearch} filter={filter} setFilter={setFilter} unreadCount={unreadCount} channels={CHANNELS} avatarColor={avatarColor} initials={initials} onOpenChat={handleOpenChat} hasMoreRemote={conversations.length < conversationTotal} onLoadMore={loadMoreConversations} />

        {!activeChat && (
          <div className="chat-empty-state">
            <span className="chat-empty-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M21 12a8 8 0 0 1-8 8H4l2.3-2.9A8 8 0 1 1 21 12z"/><path d="M8 10h8M8 14h5"/></svg></span>
            <h2>Выберите диалог</h2>
            <p>Откройте клиента слева, чтобы увидеть историю переписки и продолжить разговор.</p>
            {filteredList.length > 0 && (<button className="btn btn-primary" onClick={() => handleOpenChat(filteredList[0].id)}>Открыть первый диалог</button>)}
          </div>
        )}

        {activeChat && (
          <ChatMessagePanel activeChat={activeChat} messages={messages} isTyping={isTyping} input={input} setInput={setInput} onSend={handleSend} onAiRespond={aiRespondClick} onRetryMessage={retryMessage} hasOlderMessages={messages.length < messageTotal} onLoadOlderMessages={loadOlderMessages} onToggleAi={toggleChatAi} onBackToChats={backToChats} msgEndRef={msgEndRef} channels={CHANNELS} avatarColor={avatarColor} initials={initials} />
        )}

        {activeChat && (
          <>
            <button type="button" className="chat-details-trigger" aria-expanded={isDetailsOpen} aria-controls="chat-details-panel" onClick={() => setIsDetailsOpen(true)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="3"/><path d="M5 20a7 7 0 0 1 14 0"/></svg>
              Детали
            </button>
            <button type="button" className={`chat-details-scrim ${isDetailsOpen ? 'open' : ''}`} aria-label="Закрыть детали клиента" onClick={() => setIsDetailsOpen(false)} />
            <aside id="chat-details-panel" className={`chat-details-shell ${isDetailsOpen ? 'open' : ''}`} aria-label="Детали клиента">
              <button type="button" className="chat-details-close modal-close" aria-label="Закрыть детали клиента" onClick={() => setIsDetailsOpen(false)}>×</button>
              <ChatDetailsSidebar
                activeChat={activeChat} chatTags={chatTags} activeChatDeals={activeChatDeals}
                isDealFormOpen={isDealFormOpen} setIsDealFormOpen={setIsDealFormOpen} resetDealForm={resetDealForm}
                onUpdateChatField={updateChatField} onEditDealClick={handleEditDealClick} onUpdateDealStatus={handleUpdateDealStatus}
                handleSaveDeal={handleSaveDeal} dealTitle={dealTitle} setDealTitle={setDealTitle}
                dealAmount={dealAmount} setDealAmount={setDealAmount} dealDiscountPercent={dealDiscountPercent} setDealDiscountPercent={setDealDiscountPercent}
                dealStage={dealStage} setDealStage={setDealStage} dealItems={dealItems} editingDealId={editingDealId}
                selectedProdId={selectedProdId} setSelectedProdId={setSelectedProdId} catalog={catalog}
                onAddItemToDeal={handleAddItemToDeal} onRemoveItemFromDeal={handleRemoveItemFromDeal} onUpdateItemQty={handleUpdateItemQty}
                autopilot={autopilot} setAutopilot={setAutopilot} channels={CHANNELS} avatarColor={avatarColor} initials={initials}
              />
            </aside>
          </>
        )}
      </div>
    </section>
  );
}
